import React, {createContext, useContext} from "react"
import {formatText} from "../config/text"
import site from "../config/site.json"
import {variantPrefix} from "../config/variant"
import defaultUi from "../data/text/en/standard/ui.json"

export type Translate = (key: string, values?: Record<string, string | number>) => string

export interface VariantValue {
    language: string
    edition: string
    ui: Record<string, string>
    // Dezelfde pagina in de varianten waarin ze bestaat (ook deze zelf).
    alternates: {language: string, edition: string, path: string}[]
    // quiz-templates.json van de variant; ontbreekt op pagina's zonder eigen pageContext.
    quizTemplates?: any
    // Git-datums van de pagina (ISO), uit pageDates in gatsby-node; null als onbekend.
    dates?: {published: string, modified: string} | null
}

// Terugval voor pagina's zonder eigen pageContext (404, privacy policy, not-ready-yet).
const fallback: VariantValue = {
    language: site.defaultLanguage,
    edition: site.defaultEdition,
    ui: defaultUi,
    alternates: [],
}

const VariantContext = createContext<VariantValue>(fallback)

export const VariantProvider: React.FC<{pageContext?: any, children: React.ReactNode}> = ({pageContext, children}) => {
    const value: VariantValue = {
        language: pageContext?.language ?? fallback.language,
        edition: pageContext?.edition ?? fallback.edition,
        ui: pageContext?.ui ?? fallback.ui,
        alternates: pageContext?.alternates ?? fallback.alternates,
        quizTemplates: pageContext?.quizTemplates,
        dates: pageContext?.dates ?? null,
    }
    return <VariantContext.Provider value={value}>{children}</VariantContext.Provider>
}

export const useVariant = () => useContext(VariantContext)

export const makeT = (ui: Record<string, string>): Translate => (key, values) => {
    if (!(key in ui)) throw new Error(`Onbekende ui-sleutel: ${key}`)
    return formatText(ui[key], values)
}

export const useQuizTemplates = (): any => {
    const {quizTemplates} = useVariant()
    if (!quizTemplates) throw new Error("Deze pagina heeft geen quiz-templates in zijn pageContext")
    return quizTemplates
}

// Voorvoegsel van de variant van de pagina: "" | "/street" | "/nl" | "/nl/street"
export const useVariantPrefix = (): string => {
    const {language, edition} = useVariant()
    return variantPrefix(language, edition)
}

// t("h1.track.line2", {track}) -> tekst uit ui.json van de variant van de pagina
export const useT = (): Translate => makeT(useVariant().ui)
